import React, { useState, useEffect, useCallback, useContext, useMemo, useRef } from 'react';
import { Editor } from './EditorOld';
import { useParams, useHistory } from 'react-router-dom';
import request from 'app/api/request';
import withPage from 'app/slate/extensions/page';
import withParagraph from 'app/slate/extensions/paragraph';
import withLayout from 'app/slate/extensions/layout';
import { TemplateSidebar } from './TemplateSidebar';
import TemplateStructure from './TemplateStructure';
import { useDelayedSave } from 'app/utils/hooks/delayed-save';
import { NavbarTemplateEditor } from './navbar';
import { Spinner, SpinnerSmall } from 'app/components';
import { i18n } from 'app/utils/i18n';
import { SubNavBar } from 'app/components/editor/navbar/SubNavbar';
import { SubToolbar } from 'app/components/editor/subtoolbar/SubToolbar';
import { v4 as uuidv4 } from 'uuid';
import { UserContext } from 'app/state/contexts/UserContext';
import { useApplyTemplateDraft, useCompiledTemplate, useDocumentWithStyle, useSaveCompiledTemplate } from 'app/api/template-queries';
import { EditorModeContextProvider } from 'app/state/contexts/EditorContext';

import { useDraft, useDiscardDraft, NO_DRAFT } from 'app/api/draft-queries';
import { setDocument } from 'app/state/redux/documentSlice';
import { useEditorDispatch } from 'app/state/contexts/EditorContext';

const buildDraftContent = (draftContent = [], documentDesign, documentStyle, template) => {
  const content = [...draftContent];

  if (!content.find((s) => s.type === 'document')) {
    content.push({
      type: 'document',
      style: documentStyle.content,
      templateSuperId: documentDesign.templateSuperId,
      templateRevisionId: documentDesign.templateRevisionId,
    });
  }

  if (!content.find((s) => s.type === 'template' && s.templateSuperId === template.superId)) {
    content.push({
      ...template,
      templateSuperId: template.superId,
      type: 'template',
    });
  }

  return content;
};

const extensions = [withLayout({ markEmpty: true }), withPage(), withParagraph()];

const templateEditorStyles = `
.layout-leaf-empty {
  background: rgba(193, 210, 228, 0.23);
}

.layout-leaf {
  outline: 1px dotted rgba(77, 184, 148, 0.5);
}

/*TODO lägg till .layout-styling på hover-effekt*/
}`;

export const TemplateEditor = () => {
  const history = useHistory();

  const { documentId, templateId } = useParams();
  const { selectedOrganization } = useContext(UserContext);
  const editorDispatch = useEditorDispatch();

  // --- Query hooks ---
  const { data: templateData } = useCompiledTemplate(templateId, selectedOrganization);
  const { data: docWithStyle, refetch: refetchDocWithStyle } = useDocumentWithStyle(documentId, templateId);
  const { data: draftData } = useDraft({ documentSuperId: documentId });

  // --- Derived from queries (no state needed) ---
  const doc = docWithStyle?.doc;

  useEffect(() => {
    if (doc) {
      editorDispatch(setDocument(doc));
    }
  }, [doc, editorDispatch]);

  const baseDraftContent = useMemo(() => {
    if (!docWithStyle || !templateData || !draftData) return null;
    const draft = draftData === NO_DRAFT ? null : draftData;
    const documentDesign = docWithStyle.doc.content.design.find((s) => s.type === 'document');
    return buildDraftContent(draft?.content, documentDesign, docWithStyle.style, templateData);
  }, [docWithStyle, templateData, draftData]);

  const documentStyle = useMemo(() => {
    if (!baseDraftContent) return null;
    return baseDraftContent.find((s) => s.type === 'document')?.style;
  }, [baseDraftContent]);

  // --- Local editor state (user edits these) ---
  const [template, setTemplate] = useState(null);
  const [templateStyle, setTemplateStyle] = useState('');
  const [structureEditorContent, setStructureEditorContent] = useState();
  const [transpiledCss, setTranspiledCss] = useState('');
  const [exposedCss, setExposedCss] = useState('');
  const [errorMessage, setErrorMessage] = useState(null);
  const [shouldStoreDraft, setShouldStoreDraft] = useState(false);
  const [draftExists, setDraftExists] = useState(false);
  const [dirtyDraft, setDirtyDraft] = useState(false);
  const [isDiscarding, setIsDiscarding] = useState(false);
  const [isApplying, setIsApplying] = useState(false);

  // --- Derived: draftContent from base + local edits (replaces 3 sync effects) ---
  const draftContent = useMemo(() => {
    if (!baseDraftContent || !structureEditorContent) return null;
    return baseDraftContent.map((entry) => {
      if (entry.type === 'template' && (entry.superId === templateId || entry.templateSuperId === templateId)) {
        return { ...entry, style: templateStyle, structure: structureEditorContent[0] };
      }
      return entry;
    });
  }, [baseDraftContent, templateStyle, structureEditorContent, templateId]);

  // --- Mutations ---
  const { mutate: applyTemplateDraft } = useApplyTemplateDraft(documentId);
  const { mutate: saveCompiledTemplate } = useSaveCompiledTemplate();
  const { mutate: discardDraftMutation } = useDiscardDraft();

  // --- Initialize local state from query data (only on first load or after reset) ---
  const initKeyRef = useRef(null);
  const [resetKey, setResetKey] = useState(0);
  const currentInitKey = `${documentId}-${templateId}-${resetKey}`;

  useEffect(() => {
    if (!baseDraftContent || !templateData || !docWithStyle) return;
    if (initKeyRef.current === currentInitKey) return;
    initKeyRef.current = currentInitKey;

    const pDocument = docWithStyle.doc;
    const draft = draftData === NO_DRAFT ? null : draftData;

    setTranspiledCss(pDocument.css);
    setExposedCss(pDocument.exposedCss ?? '');
    setTemplate(templateData);
    setDraftExists(!!draft);

    const templateDraft = baseDraftContent.find((s) => s.type === 'template' && (s.superId === templateId || s.templateSuperId === templateId));
    setStructureEditorContent([templateDraft.structure]);
    setTemplateStyle(templateDraft.style);
  }, [baseDraftContent, templateData, docWithStyle, draftData, templateId, currentInitKey]);

  // --- Draft saving ---
  const [storeDraft, cancelStoreDraft] = useDelayedSave(
    useCallback(
      async (draftContent) => {
        const draft = {
          documentSuperId: doc.superId,
          documentRevisionId: doc.revisionId,
          content: draftContent,
          organization: selectedOrganization,
        };

        await request.post(`/gaby/design-draft?compiled=true`, draft);
        setDraftExists(true);
        setDirtyDraft(false);

        const { data } = await refetchDocWithStyle();
        if (data?.doc?.css) {
          setTranspiledCss(data.doc.css);
          setExposedCss(data.doc.exposedCss ?? '');
          setErrorMessage(null);
        } else {
          setErrorMessage('Error compiling sass');
        }
      },
      [selectedOrganization, doc, refetchDocWithStyle]
    )
  );

  useEffect(() => {
    if (shouldStoreDraft) {
      storeDraft(draftContent);
      setDirtyDraft(true);
      setShouldStoreDraft(false);
    }
  }, [draftContent, shouldStoreDraft, storeDraft]);

  // --- Handlers ---
  const handleTemplateClassNameChange = useCallback(
    (className) => {
      const newTemplate = {
        ...template,
        className: className.split(' ')[0],
      };
      setTemplate(newTemplate);
      setShouldStoreDraft(true);
    },
    [template]
  );

  const handleStructureChange = (value, editor) => {
    if (editor.operations.some((op) => op.type !== 'set_selection')) {
      setShouldStoreDraft(true);
    }
    setStructureEditorContent(value);

    // FIXME This is needed for change events to happen, why?
    history.replace('#');
  };

  const handleTemplateStyleChange = (style) => {
    setTemplateStyle(style);
    setShouldStoreDraft(true);
  };

  const handleDiscardDraftClick = async () => {
    cancelStoreDraft();
    setShouldStoreDraft(false);
    setIsDiscarding(true);
    setResetKey((k) => k + 1);
    discardDraftMutation(
      { superId: documentId },
      {
        onSuccess: () => {
          refetchDocWithStyle();
          setDraftExists(false);
          setIsDiscarding(false);
        },
        onError: () => {
          setIsDiscarding(false);
        },
      }
    );
  };

  const handleApplyDraftInProjectClick = async () => {
    setShouldStoreDraft(false);
    setIsApplying(true);
    const newTemplate = {
      ...template,
      style: templateStyle,
      structure: structureEditorContent[0],
      revisionId: uuidv4(),
    };

    saveCompiledTemplate(newTemplate, {
      onSuccess: (savedTemplate) => {
        setErrorMessage(null);
        setTemplate(savedTemplate);
        history.replace('?');

        applyTemplateDraft([doc.revisionId, templateId], {
          onSuccess: (response) => {
            setTranspiledCss(response.data.css);
            setExposedCss(response.data.exposedCss ?? '');
            setDraftExists(false);
            setIsApplying(false);
          },
          onError: () => {
            setIsApplying(false);
          },
        });
      },
      onError: async (e) => {
        try {
          const data = await JSON.parse(e.request.response);
          setErrorMessage(data.message);
        } catch (_) {
          setErrorMessage('Unknown error');
        }
        setIsApplying(false);
        history.replace('?');
      },
    });
  };

  // --- Loading state ---
  if (!doc || !structureEditorContent || documentStyle === undefined || documentStyle === null) {
    return <Spinner />;
  }

  const shadowedChildren = <style type="text/css">{transpiledCss + templateEditorStyles}</style>;
  const permissions = { styling: true, insert: false, languange: false };
  const showSubToolbar = Object.values(permissions).some((value) => value); // Permission check?
  const toolbarComponents = { styling: true, insert: false, languange: false };

  return (
    <EditorModeContextProvider>
      <div className={`container-fluid ps-0 ${showSubToolbar ? 'sub-navbar-padding' : ''}`}>
        <style type="text/css">{exposedCss}</style>
        <div className="d-flex">
          <Editor
            className="d-flex w-100"
            extensions={extensions}
            referenceData={null}
            onChange={handleStructureChange}
            doc={doc}
            section={structureEditorContent}
            shadowedChildren={shadowedChildren}
            leftComponent={(section) => <TemplateStructure template={template} onTemplateClassNameChange={handleTemplateClassNameChange} />}
          >
            <NavbarTemplateEditor document={doc} template={template} />
            <TemplateSidebar errorMessage={errorMessage} style={templateStyle} onStyleChange={handleTemplateStyleChange} isSaving={dirtyDraft}>
              {draftExists && (
                <div className="bg-light p-3 border border-gray-300 mt-r-2-5">
                  <div className="font-medium-header pb-r-1">{i18n('template-editor.draft-changes')}</div>
                  <button className="btn btn-sm btn-secondary me-r-1" type="button" disabled={isDiscarding || isApplying} onClick={handleDiscardDraftClick}>
                    {isDiscarding ? <SpinnerSmall /> : i18n('template-editor.button.discard-draft')}
                  </button>
                  <button
                    className="btn btn-sm btn-secondary"
                    type="button"
                    disabled={dirtyDraft || isApplying || isDiscarding}
                    onClick={handleApplyDraftInProjectClick}
                  >
                    {isApplying ? (
                      <>
                        <SpinnerSmall /> {i18n('common.saving')}
                      </>
                    ) : (
                      i18n('template-editor.button.update-project')
                    )}
                  </button>
                </div>
              )}
            </TemplateSidebar>
            <SubNavBar visibile={showSubToolbar}>
              <SubToolbar enabled={toolbarComponents} />
            </SubNavBar>
          </Editor>
        </div>
      </div>
    </EditorModeContextProvider>
  );
};
